import React, { useRef, useEffect } from 'react';
import { Home, Wrench, RefreshCw, ShieldCheck, Phone } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { services, benefits } from '../mockData';

const iconMap = {
  'home': Home,
  'wrench': Wrench,
  'refresh-cw': RefreshCw,
  'shield-check': ShieldCheck
};

export const ServicesPage = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = true;
      video.play().catch((error) => {
        console.log('Autoplay prevented:', error);
      });
    }
  }, []);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="w-full h-full object-cover opacity-30"
            poster="https://customer-assets.emergentagent.com/job_roof-booking-hub/artifacts/g9jljj6s_13506d2d-2490-4647-bf63-c0b287e8cac5.jpeg"
          >
            <source src="/dezire_montage_v2.mp4" type="video/mp4" />
          </video>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Services</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Comprehensive metal roofing solutions for residential and commercial properties across Greater Sydney
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card
                  key={service.id}
                  className="card-hover border-none shadow-lg bg-white"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardHeader>
                    <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-gray-900">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 text-base mb-6">
                      {service.description}
                    </CardDescription>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center text-sm text-gray-700">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                        Professional assessment & quote
                      </li>
                      <li className="flex items-center text-sm text-gray-700">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                        Quality materials & workmanship
                      </li>
                      <li className="flex items-center text-sm text-gray-700">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                        Comprehensive warranty included
                      </li>
                    </ul>
                    <Button
                      onClick={() => navigate('/contact')}
                      className="w-full bg-primary hover:bg-accent text-white"
                    >
                      Get Free Quote
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-primary">Metal Roofing?</span>
            </h2>
            <p className="text-lg text-gray-600">
              Discover the unmatched advantages of metal roofing for your property
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const iconMap2 = {
                'shield': ShieldCheck,
                'zap': ShieldCheck,
                'cloud-rain': ShieldCheck,
                'leaf': ShieldCheck,
                'check-circle': ShieldCheck,
                'trending-up': ShieldCheck
              };
              const IconComponent = iconMap2[benefit.icon] || ShieldCheck;
              return (
                <div
                  key={benefit.id}
                  className="group p-8 rounded-xl border-2 border-gray-100 hover:border-primary transition-all duration-300 card-hover bg-white"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-14 h-14 bg-primary/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                    <IconComponent className="w-7 h-7 text-primary group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Contact us today for a free inspection and quote
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/contact')}
            className="bg-white text-primary hover:bg-gray-100 text-lg px-8 py-6"
          >
            <Phone className="w-5 h-5 mr-2" />
            Book Free Inspection
          </Button>
        </div>
      </section>
    </div>
  );
};
